/**
 * JobForm - Configurable form organism for creating and editing both
 * work orders and inspections.
 *
 * Atomic Design role: Organism. The organism owns all form state, side
 * effects, validation, and submission logic. Layout/composition is
 * delegated to the JobFormTemplate; field rendering is delegated to
 * the section molecules (CustomerLocationSection, OnSiteContactSection,
 * AssignmentSection, BillingSection, SettingsSection, plus the
 * type-specific ProjectInformationSection and InspectionNotesSection).
 *
 * The form supports both formType variants — 'work_order' and
 * 'inspection' — via the same underlying state shape. Inspection
 * records carry the same Customer & Location, Customer Contact,
 * Assignment (priority, additional assignees), and Settings data that
 * work orders do; the unified backend (inspections table +
 * inspection_assignees junction) backs this UI. Billing is the one
 * section that stays work-order-only — see the `billing` prop below.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../services/api';
import { invalidateJobCaches } from '../../../lib/invalidateJobCaches';
import { workOrderService } from '../../../services/workOrderService';
import type { CreateWorkOrderData } from '../../../services/workOrderService';
import { inspectionService } from '../../../services/inspectionService';
import type { CreateInspectionData } from '../../../services/inspectionService';
import { contactService } from '../../../services/contactService';
import type { CreateContactData } from '../../../services/contactService';
import { checklistTemplateService } from '../../../services/checklistTemplateService';
import type { ChecklistTemplate } from '../../../services/checklistTemplateService';
import { checklistInstanceService } from '../../../services/checklistInstanceService';
import { businessLineService } from '../../../services/businessLineService';
import { useAuth } from '../../../hooks/useAuth';
import { useNotifyPrompt } from '../../../hooks/useNotifyPrompt';
import { useChecklistReplaceGuard } from '../../../hooks/useChecklistReplaceGuard';
import { withAttachedChecklist } from '../../../utils/checklistOptions';
import type { AttachedChecklist } from '../../../utils/checklistOptions';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { UnsavedChangesPrompt } from '../UnsavedChangesPrompt';
import { JobFormTemplate } from '../../templates/JobFormTemplate';
import type { JobFormErrorDetail } from '../../templates/JobFormTemplate';
import type { ContactMode } from '../../molecules/ContactFieldset';
import { emptyRule, type RecurrenceMode, type RecurrenceRule } from '../../../utils/recurrence';
import { recurrenceSeriesService } from '../../../services/recurrenceSeriesService';
import { BILLING_INFO_TAGS, LOCATION_MANAGE_ROLES, T_AND_M_SUB_TYPE, WORK_ORDER_PRIORITY_OPTIONS, SCHEDULE_TIMEZONE_OPTIONS } from '../../../utils/constants';
import { localToUtcIso, utcIsoToLocalTime } from '../../../utils/scheduleTime';
import { JOB_FORM_STYLING } from '../../../utils/formClasses';
import { AddBuildingModal } from '../AddBuildingModal';
import type { CreatedBuilding } from '../AddBuildingModal';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Props for the JobForm organism */
export interface JobFormProps {
	/** 'work_order' or 'inspection' -- determines which fields to show and API endpoint */
	formType: 'work_order' | 'inspection';
	/** Called on successful submission with the created record's ID */
	onSuccess: (id: string | number) => void;
	/** When set, the form operates in edit mode — PATCHes instead of POSTs */
	editId?: string | number;
	/** Pre-fill values for edit mode. Keys should match the API response (snake_case). */
	initialData?: Record<string, any>;
	/**
	 * Pre-select an assignable checklist template id at form open. Used by the
	 * inspection duplicate flow to carry the source inspection's attached
	 * checklist over to the new record. Edit mode resolves this on its own
	 * via checklistInstanceService.getForInspection / getForWorkOrder, so this
	 * prop is only honored when editId is unset.
	 */
	initialChecklistTemplateId?: string;
	/**
	 * Attach the newly-created work order to a Project Work Order. Only
	 * honored for formType='work_order' creation (not edit). The detail
	 * page surfaces this by linking to /work-orders/create with the FK
	 * encoded as a query param.
	 */
	projectWorkOrderId?: number | string;
	/**
	 * Hours from the parent project's pool to allocate to this WO on
	 * create. Wired up by the project create flow; ignored when
	 * projectWorkOrderId is unset. Owned by the caller so the project
	 * allocation panel (rendered outside the form) can display its
	 * total/remaining context next to the input.
	 */
	projectAllocatedHours?: number;
	/**
	 * When true, disable the Customer dropdown. Used by the Follow-Up create
	 * flow so the new work order can't drift to a different customer than its
	 * parent — the backend enforces the same-company invariant, this prop is
	 * just the UI affordance that makes the rule visible.
	 */
	lockCompany?: boolean;
	/**
	 * Reports whether the form holds unsaved edits. A modal host uses it to
	 * ask "Discard changes?" before closing (useDiscardGuard); leaving the
	 * page is handled here, by the form's own UnsavedChangesPrompt.
	 */
	onDirtyChange?: (dirty: boolean) => void;
	/** Optional extra classes forwarded onto the rendered form root. */
	className?: string;
}

/** Company detail record -- full data fetched for billing address auto-fill */
interface CompanyDetail {
	company_id: string | number;
	name: string;
	billing_address?: string;
	billing_address_line_2?: string;
	billing_city?: string;
	billing_state?: string;
	billing_zip?: string;
	billing_email?: string;
	/** Accounts-payable phone number on the customer record */
	billing_phone?: string;
}

/* ============================================
 * CONSTANTS — Static dropdown option lists
 * ============================================ */

/* Business Line + Sub Type pickers are now CMS-managed — see
   businessLineService. The hardcoded option lists that used to live
   here (BUSINESS_LINE_OPTIONS, CONSTRUCTION_TYPE_OPTIONS,
   JANITORIAL_TYPE_OPTIONS) were seeded into the database via
   BusinessLinesSeeder and are loaded at runtime in the effect below. */

/* The T&M sub-type slug, the priority list and the timezone list live in
   utils/constants now — the assign-from-the-list dialog offers the same
   choices from the same arrays. */

/** Billing info multi-select options — checkboxes for billing categories.
 *  Sourced from the shared BILLING_INFO_TAGS list so this form, the Project WO
 *  form, and the work orders list filter all speak the same vocabulary. */
const BILLING_INFO_OPTIONS: string[] = [...BILLING_INFO_TAGS];

/* The old flat FREQUENCY_OPTIONS list was retired in favor of the
   structured RecurrencePicker molecule. Schedule definitions now live
   on the recurrence_series table; the per-WO/Inspection record carries
   a recurrence_series_id back-link instead of a free-text label. */

/* ============================================
 * SHARED STYLING TOKENS — passed down to every section molecule
 * ============================================
 * One copy, in utils/formClasses, shared with the assign-from-the-list
 * dialog so its Assignment section looks exactly like this form's. */

const { labelClass, inputClass, normalBorder, errorBorder } = JOB_FORM_STYLING;

/* ============================================
 * ERROR SUMMARY MAPPING
 * ============================================ */

/**
 * Top-to-bottom visual order of the validated fields, matching the
 * section order in JobFormTemplate (Project Information → Customer &
 * Location → Assignment → Settings). The error-summary banner lists
 * failures in this order so the list reads in the same sequence the
 * user encounters the fields while scrolling.
 */
const ERROR_KEY_ORDER = [
	'projectName',
	'description',
	'businessLine',
	'companyId',
	'locationId',
	/* Require Hours sits above the assignee rows inside Assignment. */
	'hoursBucket',
	'assigneeId',
	'inspectionDate',
	'scheduledDate',
	'checklistTemplateId',
	'billingInfo',
];

/**
 * DOM-id overrides for error keys whose input id differs from the key
 * itself. Every other key doubles as the id of its input, so the
 * summary's jump links resolve by identity.
 */
const ERROR_KEY_TO_DOM_ID: Record<string, string> = {
	inspectionDate: 'scheduledDate',
};

/** Builds the ordered, linkable error list for the summary banner. */
const buildErrorDetails = (validationErrors: Record<string, string>): JobFormErrorDetail[] => {
	const rank = (key: string) => {
		const i = ERROR_KEY_ORDER.indexOf(key);
		/* Unknown keys (future validations) sort last rather than dropping. */
		return i === -1 ? ERROR_KEY_ORDER.length : i;
	};
	return Object.entries(validationErrors)
		.sort(([a], [b]) => rank(a) - rank(b))
		.map(([key, message]) => ({ id: ERROR_KEY_TO_DOM_ID[key] ?? key, message }));
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const JobForm: React.FC<JobFormProps> = ({ formType, onSuccess, editId, initialData, initialChecklistTemplateId, projectWorkOrderId, projectAllocatedHours, lockCompany = false, onDirtyChange, className }) => {
	const { user, hasAnyRole } = useAuth();
	/* Used only after creating a recurring series whose first job isn't due
	   yet — there's no job to hand to onSuccess, so go to the series page. */
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const isEditMode = Boolean(editId);

	/* Unsaved edits. Flipped on the first change event that bubbles up the
	   form (typing, a select, a checkbox), cleared right before a successful
	   save hands off — which is also what releases the leave-page prompt for
	   the navigation that follows. Loading initialData never trips it: that
	   is state, not an event. */
	const [isDirty, setIsDirty] = useState(false);
	useEffect(() => {
		onDirtyChange?.(isDirty);
	}, [isDirty, onDirtyChange]);

	/* "Notify users" gate. Saving fans out email — to a new assignee, to
	 * whoever watches this customer, to the customer contact on a completed
	 * inspection — and admins / super admins get to see that list and edit it
	 * before it happens. Everyone else never sees the prompt and saves exactly
	 * as before. */
	const jobLabel = formType === 'work_order' ? 'work order' : 'inspection';
	const { requestNotifyDecision, notifyPrompt } = useNotifyPrompt({ lead: `Saving this ${jobLabel} will email` });
	/* "Replace this checklist?" — asked before a save swaps out a checklist
	   that already holds work, which the server otherwise refuses. */
	const { confirmReplace, replacePrompt } = useChecklistReplaceGuard(formType);

	/* Invalidate the cached server reads touched by a create/update so the
	 * list, Dashboard and calendar surfaces reflect the change when the user
	 * lands on them. Recurrence series are always included because a job can
	 * spawn one. The full set lives in invalidateJobCaches — the Dashboard
	 * reads two caches this form writes to but does not own, and missing them
	 * is what left a newly saved work order off the Dashboard. */
	const invalidateAfterSave = useCallback(() => {
		invalidateJobCaches(queryClient, formType, {
			includeRecurrence: true,
			projectWorkOrderId,
		});
	}, [queryClient, formType, projectWorkOrderId]);

	/* ============================================
	 * SHARED FORM STATE
	 * ============================================
	 * The state shape is now identical for both form types — any
	 * inspection-specific or work-order-specific gating happens at
	 * render time inside the section molecules or at submit time
	 * when building the API payload. */

	/* ---- Customer & Location ---- */
	const [companyId, setCompanyId] = useState('');
	const [locationId, setLocationId] = useState('');
	const [projectAddress, setProjectAddress] = useState('');

	/* ---- "Add a New Building" modal (opened from the Building dropdown) ---- */
	/* The customer's building often isn't in the system yet when a job is being
	   written up. Creating it inline keeps the half-filled form intact instead
	   of sending the user off to the Buildings page and back. */
	const [addBuildingOpen, setAddBuildingOpen] = useState(false);
	/* Bumped after a create so CompanyLocationFieldset re-fetches its building
	   list — the fieldset owns that fetch, keyed on company + this token. */
	const [buildingsRefreshToken, setBuildingsRefreshToken] = useState(0);

	/* ---- Follow-Up parent IDs (work_order only) ---- */
	/* These flow in via initialData when the form was opened from a
	   "Create Follow-Up" kebab action. Both stay null on regular WOs;
	   exactly one is set on a follow-up. The values are passed through
	   on the create payload so the backend can stamp the parent link and
	   auto-tick "Non Billable" in billing_info. */
	const [parentWorkOrderId, setParentWorkOrderId] = useState<number | null>(null);
	const [parentInspectionId, setParentInspectionId] = useState<number | null>(null);

	/* ---- Project Information ---- */
	/* projectName + description are only surfaced on the work_order
	   form (the inspection form labels its title field differently);
	   businessLine is shared across both form types. subType is
	   work_order-only — inspections have no sub-type concept. */
	const [projectName, setProjectName] = useState('');
	const [description, setDescription] = useState('');
	const [businessLine, setBusinessLine] = useState('');
	/** Sub Type — slug from the CMS-managed business_line_sub_types row
	 *  under the selected businessLine. Replaces the old per-line
	 *  constructionType + janitorialType state pair. Work orders only;
	 *  stays '' for the whole lifetime of an inspection form. */
	const [subType, setSubType] = useState('');
	/** CMS-managed line + sub-type tree, loaded once on mount. The
	 *  pickers below filter by active=true; the cache also powers the
	 *  default-checklist auto-fill effect further down. */
	const [businessLines, setBusinessLines] = useState<import('../../../services/businessLineService').BusinessLine[]>([]);
	/** Per-assignee hours map for T&M jobs (keyed by user id as string).
	 *  Edits buffered as strings so the input can be empty mid-typing
	 *  without coercing to 0. Submitted as a numeric map. */
	const [assigneeHours, setAssigneeHours] = useState<Record<string, string>>({});
	/** "Require Hours" — opt-in hour tracking on ANY work order, not just
	 *  T&M ones. When on, the Assignment section swaps its assignee
	 *  pickers for the per-assignee hours editor and every assignee has to
	 *  carry hours before the record can be saved, submitted or completed.
	 *  Forced on (and locked) by the T&M sub-type — see the effect below. */
	const [requiresHours, setRequiresHours] = useState(false);
	/** Bucket of hours this work order draws from. Buffered as a string so
	 *  the field can be empty mid-typing; persists to
	 *  work_orders.allocated_hours. Work orders only. */
	const [hoursBucket, setHoursBucket] = useState('');

	/* ---- Assignment ---- */
	const [assigneeId, setAssigneeId] = useState('');
	const [additionalAssigneeIds, setAdditionalAssigneeIds] = useState<string[]>([]);
	const [priority, setPriority] = useState('medium');
	/* Single date field for both form types — the AssignmentSection
	   labels it "Scheduled Date" for work orders and "Inspection Date"
	   for inspections. Inspections default to today. */
	const [scheduledDate, setScheduledDate] = useState(() =>
		formType === 'inspection' ? new Date().toISOString().split('T')[0] : ''
	);
	const [startTime, setStartTime] = useState('');
	const [endTime, setEndTime] = useState('');
	const [scheduleTimezone, setScheduleTimezone] = useState(() => user?.timezone || 'America/New_York');

	/* ---- Customer Contact (3-mode picker, shared) ----
	   Defaults to selecting from the customer's contact database — picking
	   the customer first and then one of their contacts is the primary
	   flow; creating a brand-new contact is the fallback. */
	const [contactMode, setContactMode] = useState<ContactMode>('existing_contact');
	const [contactUserId, setContactUserId] = useState('');
	const [selectedContactId, setSelectedContactId] = useState('');
	const [contactName, setContactName] = useState('');
	const [contactPhone, setContactPhone] = useState('');
	const [contactEmail, setContactEmail] = useState('');
	const [newContactFirstName, setNewContactFirstName] = useState('');
	const [newContactLastName, setNewContactLastName] = useState('');
	const [newContactTitle, setNewContactTitle] = useState('');

	/* Clear a selected existing contact when the customer changes — contacts
	   belong to exactly one company, so carrying the previous customer's
	   contact over would be rejected by the backend's company-match rule.
	   contactUserId is cleared alongside it in both picker modes: "Existing
	   Contact" can resolve to one of the company's platform users, and "Select
	   User" only ever offers the selected customer's users. Either way the
	   person already picked is not necessarily on the new customer's roster,
	   and leaving the id behind would show a name the dropdown can no longer
	   find.
	   The ref skips the initial ''→value transition (mount, edit hydration,
	   duplicate/follow-up prefill) so prefilled selections survive; later
	   re-runs from other dep changes are no-ops because prev === companyId. */
	const prevContactCompanyIdRef = useRef('');
	useEffect(() => {
		const prev = prevContactCompanyIdRef.current;
		prevContactCompanyIdRef.current = companyId;
		if (!prev || prev === companyId) return;
		if (selectedContactId || contactUserId || contactName || contactPhone || contactEmail) {
			setSelectedContactId('');
			if (contactMode !== 'new_contact') {
				setContactUserId('');
				setContactName('');
				setContactPhone('');
				setContactEmail('');
			}
		}
	}, [companyId, selectedContactId, contactUserId, contactMode, contactName, contactPhone, contactEmail]);

	/* ---- Inspection Notes (inspection only) ---- */
	const [generalNotes, setGeneralNotes] = useState('');

	/* ---- Billing (shared) ---- */
	const [billingInfo, setBillingInfo] = useState<string[]>([]);
	const [billingName, setBillingName] = useState('');
	const [billingAddress, setBillingAddress] = useState('');
	const [billingAddressLine2, setBillingAddressLine2] = useState('');
	const [billingCity, setBillingCity] = useState('');
	const [billingState, setBillingState] = useState('');
	const [billingZip, setBillingZip] = useState('');
	const [billingPhone, setBillingPhone] = useState('');
	/* "Send to Client" — inspections only. Defaults OFF: the inspection is
	   reviewed internally before the property manager is emailed. */
	const [notifyClient, setNotifyClient] = useState(false);
	const [billingEmail, setBillingEmail] = useState('');
	const [billingNotes, setBillingNotes] = useState('');
	const [isBillingEditable, setIsBillingEditable] = useState(false);
	/* eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept
	   for parity with companyDetail's effect side; not currently read
	   anywhere, but tracked so a future enhancement can render the
	   raw company billing record without re-fetching. */
	const [, setCompanyDetail] = useState<CompanyDetail | null>(null);

	/* ---- Settings (shared) ---- */
	const [signatureRequired, setSignatureRequired] = useState(false);
	/* Recurrence — the legacy free-text `frequency` string has been
	   replaced by a structured rule object produced by the
	   RecurrencePicker molecule. `recurrenceMode` is the on/off + UX
	   discriminator (none/preset/custom); `recurrenceRule` carries the
	   serializable rule that goes to the backend when set. */
	const [recurrenceMode, setRecurrenceMode] = useState<RecurrenceMode>('none');
	const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule>(() => emptyRule());

	/* ---- Estimate (shared) ---- */
	/* Stored as strings so empty inputs round-trip cleanly; converted
	   to numbers at submit time. */
	const [estimatedCost, setEstimatedCost] = useState('');
	const [estimateMin, setEstimateMin] = useState('');
	const [estimateMax, setEstimateMax] = useState('');

	/* ---- Checklist (shared) ---- */
	const [checklistTemplateId, setChecklistTemplateId] = useState<string>('');
	const [assignableChecklists, setAssignableChecklists] = useState<ChecklistTemplate[]>([]);
	const [originalChecklistTemplateId, setOriginalChecklistTemplateId] = useState<string>('');
	/** The checklist attached to the job being edited — its title, whether
	 *  its version is still current, and how many items hold work. Keeps it
	 *  in the dropdown even when retired, and drives the replace warning. */
	const [attachedChecklist, setAttachedChecklist] = useState<AttachedChecklist | null>(null);

	/* ---- Submission state ---- */
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [submitError, setSubmitError] = useState<string | null>(null);
	/* Per-field entries listed inside the banner — each links to its field. */
	const [submitErrorDetails, setSubmitErrorDetails] = useState<JobFormErrorDetail[]>([]);
	/* Bumped on every failed submit so the template scrolls/focuses the
	   banner again even when the message text is identical to the last
	   attempt (an unchanged prop wouldn't re-run its effect). */
	const [submitErrorNonce, setSubmitErrorNonce] = useState(0);

	/** Surfaces the top banner and triggers the template's scroll-to-it. */
	const showSubmitError = (message: string, details: JobFormErrorDetail[] = []) => {
		setSubmitError(message);
		setSubmitErrorDetails(details);
		setSubmitErrorNonce((n) => n + 1);
	};

	/* ============================================
	 * EDIT MODE — Pre-fill state from initialData
	 * ============================================
	 * The API returns snake_case keys (and sometimes camelCase via the
	 * client transformer). Use the v() helper to read both shapes. */

	useEffect(() => {
		if (!initialData) return;
		const d = initialData;

		const v = (camel: string, snake?: string): any =>
			d[camel] ?? (snake ? d[snake] : undefined) ?? '';

		/* Shared identity fields */
		setCompanyId(String(v('companyId', 'company_id')));
		setLocationId(String(v('locationId', 'location_id')));

		/* Follow-Up parent IDs — only present when the create page seeded
		   them from a ?follow_up_from_wo / ?follow_up_from_inspection
		   query param. Stored as numbers to match the backend FK type. */
		const rawParentWoId = d.parentWorkOrderId ?? d.parent_work_order_id;
		const rawParentInspId = d.parentInspectionId ?? d.parent_inspection_id;
		if (rawParentWoId) setParentWorkOrderId(Number(rawParentWoId));
		if (rawParentInspId) setParentInspectionId(Number(rawParentInspId));
		/* Robust assignee lookup — work order responses use the
		   normalizeWorkOrder-flattened `assignedToId`; inspections use
		   the camelized `assigneeId`; fall back to the raw snake_case
		   key and finally to the nested `assignee` relation's id so any
		   API shape resolves to a usable primary-assignee id. Without
		   this the inspection edit prefill silently dropped the
		   assignee, which made `validateForm()` fail (Assignee is
		   required) and made the Save button look broken. */
		const initialAssigneeId = d.assigneeId
			?? d.assignedToId
			?? d.assignee_id
			?? (d.assignee && (d.assignee as any).id)
			?? '';
		setAssigneeId(initialAssigneeId ? String(initialAssigneeId) : '');
		setProjectAddress(v('projectAddress', 'project_address'));

		/* Shared assignment + scheduling fields */
		setPriority(v('priority') || 'medium');
		const rawDateValue = formType === 'work_order'
			? v('scheduledDate', 'scheduled_date')
			: v('inspectionDate', 'inspection_date');
		if (rawDateValue) {
			/* The API can serialize Eloquent date casts as either pure
			   YYYY-MM-DD strings or full ISO 8601 datetimes (e.g.
			   "2026-05-08T00:00:00.000000Z"). HTML date inputs require
			   YYYY-MM-DD, so strip any trailing time portion before
			   seeding state. */
			const dateOnly = String(rawDateValue).split('T')[0];
			setScheduledDate(dateOnly);
		}

		/* Shared contact fields */
		setContactName(v('contactName', 'contact_name'));
		setContactPhone(v('contactPhone', 'contact_phone'));
		setContactEmail(v('contactEmail', 'contact_email'));
		const initialContactId = d.contactId ?? d.contact_id;
		if (initialContactId) {
			setSelectedContactId(String(initialContactId));
			setContactMode('existing_contact');
		} else if (v('contactName', 'contact_name')) {
			/* No contact_id but a name is present — surface the data in
			   the existing-contact mode so the inputs stay populated and
			   editable. */
			setContactMode('existing_contact');
		}

		/* Shared billing fields */
		const bi = d.billingInfo ?? d.billing_info;
		if (Array.isArray(bi)) setBillingInfo(bi);
		setBillingName(v('billingName', 'billing_name'));
		setBillingAddress(v('billingAddress', 'billing_address'));
		setBillingAddressLine2(v('billingAddressLine2', 'billing_address_line_2'));
		setBillingCity(v('billingCity', 'billing_city'));
		setBillingState(v('billingState', 'billing_state'));
		setBillingZip(v('billingZip', 'billing_zip'));
		setBillingPhone(v('billingPhone', 'billing_phone'));
		setNotifyClient(Boolean((initialData as any)?.notifyClient ?? (initialData as any)?.notify_client ?? false));
		setBillingEmail(v('billingEmail', 'billing_email'));
		setBillingNotes(v('billingNotes', 'billing_notes'));

		/* Shared settings fields */
		setSignatureRequired(Boolean(d.signatureRequired ?? d.signature_required));
		/* Legacy `frequency` string is intentionally NOT hydrated here
		   any more — recurring records are now managed via the
		   /repeats pages, not the per-WO/Inspection form. Edit-mode of
		   an existing series occurrence still works (the user just
		   can't change the recurrence from the form), and standalone
		   records simply start with mode='none'. */

		/* Shared estimate fields — preserve as strings for input round-tripping */
		setEstimatedCost(d.estimatedCost ?? d.estimated_cost ?? '');
		setEstimateMin(d.estimateMin ?? d.estimate_min ?? '');
		setEstimateMax(d.estimateMax ?? d.estimate_max ?? '');

		/* Additional assignees — extract IDs from the assignees relation */
		const assignees = d.assignees;
		if (Array.isArray(assignees)) {
			const primaryId = String(d.assignee_id ?? d.assignedToId ?? '');
			const additionalIds = assignees
				.filter((a: any) => String(a.id) !== primaryId)
				.map((a: any) => String(a.id));
			setAdditionalAssigneeIds(additionalIds);
		}

		/* Start/end times — convert UTC ISO strings back to local HH:MM
		   for the time inputs, using the prefilled scheduleTimezone. */
		const rawStart = d.startTime ?? d.start_time;
		const rawEnd = d.endTime ?? d.end_time;
		if (rawStart) setStartTime(utcIsoToLocalTime(rawStart, scheduleTimezone));
		if (rawEnd) setEndTime(utcIsoToLocalTime(rawEnd, scheduleTimezone));

		/* Project info — title/description and sub_type are work_order-only
		   fields; business_line is shared so it hydrates for both form
		   types. The fall-through `?? construction_type ??
		   janitorial_type` keeps edit-mode hydration working for old
		   records that pre-date the unified sub_type column. */
		setBusinessLine(v('businessLine', 'business_line'));
		if (formType === 'work_order') {
			setProjectName(v('projectName', 'project_name'));
			setDescription(v('description'));
			const subTypeFromPayload = (d.subType ?? d.sub_type
				?? d.constructionType ?? d.construction_type
				?? d.janitorialType ?? d.janitorial_type ?? '') as string;
			setSubType(subTypeFromPayload);

			/* Require Hours + its bucket. The T&M fall-through mirrors the
			   server's own rule: a T&M job tracks hours whether or not the
			   stored flag says so, which keeps records created before the
			   flag existed opening in the right mode. */
			const requiresHoursFromPayload = d.requiresHours ?? d.requires_hours;
			setRequiresHours(
				requiresHoursFromPayload != null
					? Boolean(requiresHoursFromPayload)
					: subTypeFromPayload === T_AND_M_SUB_TYPE
			);
			const bucketFromPayload = d.allocatedHours ?? d.allocated_hours;
			if (bucketFromPayload != null) setHoursBucket(String(bucketFromPayload));

			/* In edit mode, pull per-assignee hours off the eager-loaded
			   assignees pivot so the T&M editor renders with the current
			   values. Each row in initialData.assignees has a `pivot`
			   object that carries allocated_hours. */
			const assignees = d?.assignees as Array<any> | undefined;
			if (Array.isArray(assignees) && assignees.length > 0) {
				const next: Record<string, string> = {};
				for (const a of assignees) {
					const uid = String(a?.id ?? a?.user_id ?? '');
					const pivot = a?.pivot ?? {};
					const hours = pivot.allocated_hours ?? pivot.allocatedHours;
					if (uid && hours != null) next[uid] = String(hours);
				}
				if (Object.keys(next).length > 0) setAssigneeHours(next);
			}
		}

		/* Inspection-specific fields */
		if (formType === 'inspection') {
			setGeneralNotes(v('generalNotes', 'general_notes'));
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialData]);

	/* ============================================
	 * SIDE EFFECTS — Company detail for billing auto-fill
	 * ============================================
	 * The work order form's Billing section auto-fills from the
	 * selected company. In edit/duplicate mode the billing fields are
	 * already populated from the source record, so we skip the first
	 * auto-fill pass and only refresh when the user explicitly changes
	 * the company.
	 *
	 * Inspections have no Billing section, so the whole effect is a
	 * no-op there — bailing early also saves the company detail fetch
	 * on every company change. */
	const hasPrefilledBilling = useRef(isEditMode || Boolean(initialData));
	useEffect(() => {
		if (formType !== 'work_order') return;

		if (!companyId) {
			setCompanyDetail(null);
			setBillingAddress('');
			setBillingAddressLine2('');
			setBillingCity('');
			setBillingState('');
			setBillingZip('');
			setBillingEmail('');
			setBillingPhone('');
			setBillingName('');
			setIsBillingEditable(false);
			return;
		}

		const loadCompanyDetail = async () => {
			try {
				const res = await api.get<any>(`/companies/${companyId}`);
				const company: CompanyDetail = res.data?.data ?? res.data;
				setCompanyDetail(company);
				if (hasPrefilledBilling.current) {
					hasPrefilledBilling.current = false;
				} else {
					setBillingName(company.name || '');
					setBillingAddress(company.billing_address || '');
					setBillingAddressLine2(company.billing_address_line_2 || '');
					setBillingCity(company.billing_city || '');
					setBillingState(company.billing_state || '');
					setBillingZip(company.billing_zip || '');
					setBillingEmail(company.billing_email || '');
					/* Billing phone now lives on the customer record too, so it
					   auto-fills alongside the rest of the billing block instead
					   of having to be retyped on every work order. */
					setBillingPhone(company.billing_phone || '');
					setIsBillingEditable(false);
				}
			} catch (err) {
				console.error('[JobForm] Failed to load company detail for billing auto-fill:', err);
			}
		};

		loadCompanyDetail();
	}, [companyId, formType]);

	/* ============================================
	 * SIDE EFFECTS — Auto-enable signature for construction
	 * ============================================
	 * Business rule: all construction work orders require customer
	 * sign-off. Inspections aren't gated by business line, so this only
	 * applies to work orders. */
	useEffect(() => {
		if (formType === 'work_order' && businessLine === 'construction') {
			setSignatureRequired(true);
		}
	}, [businessLine, formType]);

	/* ============================================
	 * SIDE EFFECTS — Auto-check the "T&M" billing-info tag when the
	 * job's subtype is T&M. The user can still uncheck it manually if
	 * they explicitly want to track the job under a different billing
	 * category, but the default keeps billing in sync with the subtype.
	 * ============================================ */
	useEffect(() => {
		/* T&M is a system sub-type slug seeded under both Janitorial and
		   Construction. Treat any selection of 't_and_m' as the T&M
		   trigger so a CMS-added line can opt in by reusing the slug. */
		if (subType !== T_AND_M_SUB_TYPE) return;
		setBillingInfo((prev) => (prev.includes('T&M') ? prev : [...prev, 'T&M']));
		/* A T&M job is billed on time, so hour tracking isn't optional on
		   one — the checkbox renders ticked and disabled. Setting the state
		   too (rather than relying on the derived flag alone) means the
		   value posted matches what the user is looking at. Switching AWAY
		   from T&M deliberately leaves it on: the hours already entered
		   stay meaningful, and the user can untick the box themselves. */
		setRequiresHours(true);
	}, [subType]);

	/* ============================================
	 * SIDE EFFECTS — Load CMS-managed Business Lines tree
	 * ============================================ */
	/* Fetched once on mount and held for the lifetime of the form so
	 * the Business Line + Sub Type pickers can render synchronously and
	 * the default-checklist auto-fill effect below can resolve a
	 * (line, sub_type) selection to its default checklist id without
	 * a per-keystroke fetch. */
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await businessLineService.list(true);
				if (cancelled) return;
				const rows = (res.data as any)?.data ?? [];
				setBusinessLines(rows);
			} catch {
				/* Non-fatal — the pickers stay empty and the user gets
				   the placeholder "Select business line" state. */
			}
		})();
		return () => { cancelled = true; };
	}, []);

	/* ============================================
	 * SIDE EFFECTS — Auto-fill checklist from line/sub-type default
	 * ============================================ */
	/* When the user picks a Business Line + Sub Type, look up the
	 * default checklist sitting on that row and pre-select it in the
	 * checklist picker. Only fires during create (edit mode resolves
	 * the attached checklist from the existing instance via a separate
	 * effect). The user can still override the selection. */
	useEffect(() => {
		// Skip when a duplicate seeded an explicit checklist — otherwise this
		// effect re-runs once businessLines finishes loading and overwrites the
		// carried-over checklist with the business line's default_checklist_id.
		if (isEditMode || initialChecklistTemplateId || !businessLine || businessLines.length === 0) return;

		const line = businessLines.find((bl) => bl.slug === businessLine);
		if (!line) return;

		/* Sub-type default takes precedence over line-level default.
		   subType is always '' on inspections, so those fall straight
		   through to the line-level default. */
		const sub = subType ? (line.sub_types ?? []).find((st) => st.slug === subType) : null;
		const defaultId = sub?.default_checklist_id ?? line.default_checklist_id ?? null;

		if (defaultId != null) {
			setChecklistTemplateId(String(defaultId));
		}
	}, [businessLine, subType, businessLines, isEditMode, initialChecklistTemplateId]);

	/* ============================================
	 * SIDE EFFECTS — Load assignable checklist templates
	 * ============================================ */
	useEffect(() => {
		let cancelled = false;
		async function load() {
			try {
				const res = await checklistTemplateService.assignable(formType);
				if (cancelled) return;
				const rows = ((res.data as any)?.data ?? []) as ChecklistTemplate[];
				setAssignableChecklists(rows);
			} catch {
				/* Non-fatal — the dropdown stays empty */
			}
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [formType]);

	/* ============================================
	 * SIDE EFFECTS — Prefill assigned checklist on edit
	 * ============================================
	 * The WO/Inspection record itself doesn't carry the source template
	 * id (it lives on the related ChecklistInstance). Fetch it
	 * separately when editing so the dropdown reflects what's currently
	 * assigned and we can detect a change on save. */
	useEffect(() => {
		if (!isEditMode || !editId) return;
		let cancelled = false;
		async function load() {
			try {
				const res = formType === 'work_order'
					? await checklistInstanceService.getForWorkOrder(String(editId))
					: await checklistInstanceService.getForInspection(String(editId));
				if (cancelled) return;
				const instance = (res.data as any)?.data;
				const sourceId = instance?.sourceChecklistId ? String(instance.sourceChecklistId) : '';
				setChecklistTemplateId(sourceId);
				setOriginalChecklistTemplateId(sourceId);
				setAttachedChecklist(sourceId ? {
					id: sourceId,
					title: instance?.sourceChecklist?.title ?? 'Current checklist',
					/* A deleted template leaves no source row — treat it as retired. */
					isActive: Boolean(instance?.sourceChecklist?.isActive),
					progressCount: Number(instance?.progressCount ?? 0),
				} : null);
			} catch {
				/* Non-fatal — dropdown stays empty */
			}
		}
		load();
		return () => {
			cancelled = true;
		};
	}, [isEditMode, editId, formType]);

	/* ============================================
	 * SIDE EFFECTS — Seed checklist template from duplicate source
	 * ============================================
	 * The inspection duplicate flow (InspectionCreatePage with
	 * ?duplicateFrom=:id) fetches the source's checklist instance on the
	 * page level and forwards the source template id here. We seed only
	 * the working `checklistTemplateId` and intentionally leave
	 * `originalChecklistTemplateId` blank so the submit path detects a
	 * change and assigns the template to the new inspection. */
	useEffect(() => {
		if (isEditMode) return;
		if (!initialChecklistTemplateId) return;
		setChecklistTemplateId(initialChecklistTemplateId);
	}, [isEditMode, initialChecklistTemplateId]);

	/* ============================================
	 * HANDLERS
	 * ============================================ */

	/**
	 * Persist the contact currently typed into "New Contact" mode and return
	 * its id.
	 *
	 * Contacts used to be created only at work order submit time, which meant
	 * a contact typed into the form was lost if the form was abandoned or
	 * failed validation, and never appeared in the customer's contact list.
	 * The fieldset now offers "Save contact to customer" up front, and this is
	 * the same routine the submit path uses.
	 *
	 * createdContactIdRef makes it idempotent: if the work order submit fails
	 * after the contact was created, re-submitting reuses that contact instead
	 * of minting a duplicate every attempt.
	 */
	const createdContactIdRef = useRef<string | null>(null);

	const persistNewContact = useCallback(async (): Promise<string | null> => {
		if (!companyId) return null;
		if (!newContactFirstName.trim() && !newContactLastName.trim()) return null;
		if (createdContactIdRef.current) return createdContactIdRef.current;

		const contactPayload: CreateContactData = {
			first_name: newContactFirstName.trim(),
			last_name: newContactLastName.trim(),
			company_id: companyId,
		};
		if (contactPhone.trim()) contactPayload.phone = contactPhone.trim();
		if (contactEmail.trim()) contactPayload.email = contactEmail.trim();
		if (newContactTitle.trim()) contactPayload.title = newContactTitle.trim();

		const contactRes = await contactService.create(contactPayload);
		const created = contactRes.data?.data ?? contactRes.data;
		if (!created?.id) return null;

		const newId = String(created.id);
		createdContactIdRef.current = newId;
		/* Keep the snapshot fields in step with what was saved so the work
		   order carries the same name/phone/email as the contact record. */
		setContactName(`${newContactFirstName.trim()} ${newContactLastName.trim()}`.trim());
		return newId;
	}, [companyId, newContactFirstName, newContactLastName, newContactTitle, contactPhone, contactEmail]);

	/** Resetting dependent fields when the company changes. */
	const handleCompanyChange = useCallback((newCompanyId: string) => {
		setCompanyId(newCompanyId);
		setLocationId('');
		setProjectAddress('');
		/* A contact created for the previous customer must not be reused for
		   this one — drop the memo so a fresh contact is created if needed. */
		createdContactIdRef.current = null;
	}, []);

	/**
	 * Picks up a building created from the inline "Add a New Building" modal:
	 * refreshes the dropdown, selects the new building, and fills the Project
	 * Address from it — the fieldset's own auto-fill can't fire here because
	 * the selection didn't come from the dropdown.
	 */
	const handleBuildingCreated = useCallback((building: CreatedBuilding) => {
		setBuildingsRefreshToken((token) => token + 1);
		setLocationId(String(building.id));
		/* Same comma-joined shape the dropdown auto-fill produces. Left alone
		   when the building was saved without an address — an empty override
		   would wipe whatever the user already typed. */
		const parts = [building.address, building.city, building.state, building.zip].filter(Boolean);
		if (parts.length > 0) setProjectAddress(parts.join(', '));
	}, []);

	/** Toggles a billing-info tag in the multi-select array. */
	const toggleBillingInfo = useCallback((option: string) => {
		setBillingInfo((prev) => prev.includes(option) ? prev.filter((item) => item !== option) : [...prev, option]);
	}, []);

	/* ============================================
	 * DERIVED — Require Hours
	 * ============================================ */

	/** Whether this work order is T&M, which forces hour tracking on and
	 *  makes the hour bucket contractual rather than optional. */
	const isTimeAndMaterials = formType === 'work_order' && subType === T_AND_M_SUB_TYPE;

	/** Whether hour tracking applies. The T&M clause is what makes the
	 *  checkbox's disabled state honest — the behavior is on even in the
	 *  instant before the force-on effect has run. */
	const tracksHours = formType === 'work_order' && (requiresHours || isTimeAndMaterials);

	/** On a child of a Project Work Order, the create page's own
	 *  "Estimated Hours" panel already writes work_orders.allocated_hours.
	 *  The bucket here would be a second input for one column, so it steps
	 *  aside and points at the panel instead. */
	const hoursBucketOwnedByProject = Boolean(projectWorkOrderId);

	/** Numeric bucket for the hours editor's "x / y hrs" readout. Prefers
	 *  the page-level project allocation when that owns the field. */
	const hoursBucketValue = hoursBucketOwnedByProject
		? projectAllocatedHours
		: (Number(hoursBucket) > 0 ? Number(hoursBucket) : undefined);

	/* ============================================
	 * VALIDATION
	 * ============================================ */

	/** Runs all client-side validations. Sets the per-field `errors`
	 *  state and returns the same record so the caller can build the
	 *  banner's error list from it. Empty record ⇒ valid. */
	const validateForm = (): Record<string, string> => {
		const newErrors: Record<string, string> = {};

		/* Shared validations */
		if (!companyId) newErrors.companyId = 'Company is required';
		if (!locationId) newErrors.locationId = 'Building/location is required';
		if (!assigneeId) newErrors.assigneeId = 'Assignee is required';
		/* Checklist is required across both Work Orders and Inspections —
		   every job needs an attached checklist so the field tech / inspector
		   has a defined set of items to work from. The backend recurrence
		   validator also enforces this on the template. */
		if (!checklistTemplateId) newErrors.checklistTemplateId = 'Checklist is required';

		/* Work-order-specific validations */
		if (formType === 'work_order') {
			if (!projectName.trim()) newErrors.projectName = 'Project name is required';
			if (!description.trim()) newErrors.description = 'Description is required';
			if (!businessLine) newErrors.businessLine = 'Business line is required';

			/* Require Hours. The record only answers "who worked on it and
			   for how long" once EVERY assignee carries a positive number —
			   a three-person crew with hours on one of them would under-bill
			   the job. The server enforces the same rule; this is the copy
			   that names the people so the user can fix it in place. */
			if (tracksHours) {
				const crew = [assigneeId, ...additionalAssigneeIds].filter(Boolean);
				if (crew.length === 0) {
					newErrors.assigneeId = 'Assign at least one person and record their hours';
				} else {
					const missing = crew.filter((uid) => {
						const value = assigneeHours[uid];
						return value == null || value === '' || !(Number(value) > 0);
					});
					if (missing.length > 0) {
						newErrors.assigneeId = `Record hours for every assignee — ${missing.length} still missing`;
					}
				}

				/* The bucket is contractual on T&M and purely informational
				   elsewhere, so only T&M gets a hard requirement. */
				if (isTimeAndMaterials && !hoursBucketOwnedByProject && !(Number(hoursBucket) > 0)) {
					newErrors.hoursBucket = 'Enter the bucket of hours for this T&M work order';
				}
			}

			/* At least one Billing Info tag. The server requires billing_info
			   on any work order that isn't a child of a project — the parent
			   owns billing classification for the whole group — and refused a
			   tagless save with "The billing info field is required when
			   project work order id is not present.", naming a field this form
			   never shows. The condition here is the same one that decides
			   whether the Billing section renders at all, so the rule can only
			   fire on a section the user can see.

			   Create only, matching the server: update() validates
			   billing_info as `sometimes|array`, so an older record that
			   carries no tags stays editable. */
			if (!isEditMode && !hoursBucketOwnedByProject && billingInfo.length === 0) {
				newErrors.billingInfo = 'Pick at least one billing classification';
			}
		}

		/* Inspection-specific validations */
		if (formType === 'inspection') {
			if (!scheduledDate) newErrors.inspectionDate = 'Inspection date is required';
		}

		setErrors(newErrors);
		return newErrors;
	};

	/*
	 * Clear a field's error as soon as the user fixes it.
	 *
	 * Errors were only ever written by a submit attempt and never pruned, so a
	 * corrected field kept its red border and message until the next submit.
	 * On mobile — where the banner, the field, and the Save button are rarely
	 * on screen together — that read as the form being stuck: everything had
	 * been filled in and the app still looked like it was refusing.
	 *
	 * This only ever REMOVES errors. New ones still come from an explicit
	 * submit, so the form never starts shouting at someone who is mid-typing.
	 */
	const requiredFieldValues: Record<string, unknown> = {
		companyId,
		locationId,
		assigneeId,
		checklistTemplateId,
		billingInfo: billingInfo.length > 0 ? billingInfo : '',
		projectName: projectName.trim(),
		description: description.trim(),
		businessLine,
		inspectionDate: scheduledDate,
		scheduledDate,
	};

	useEffect(() => {
		setErrors((prev) => {
			const keys = Object.keys(prev);
			if (keys.length === 0) return prev;

			const next: Record<string, string> = {};
			let changed = false;
			for (const key of keys) {
				const value = requiredFieldValues[key];
				// Only prune fields this effect actually knows how to judge;
				// anything else (server-side messages) is left alone.
				const isResolvable = key in requiredFieldValues;
				if (isResolvable && value) {
					changed = true;
					continue;
				}
				next[key] = prev[key];
			}
			return changed ? next : prev;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, locationId, assigneeId, checklistTemplateId, billingInfo, projectName, description, businessLine, scheduledDate]);

	/* ============================================
	 * SUBMISSION
	 * ============================================ */

	/* The dropdown's options: what this person may assign, plus the checklist
	   already attached when it isn't among them (a retired version, or one
	   they can't see) — so it never reads as if nothing were attached. */
	const checklistOptions = useMemo(
		() => withAttachedChecklist(assignableChecklists, attachedChecklist),
		[assignableChecklists, attachedChecklist],
	);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitError(null);
		setSubmitErrorDetails([]);

		/* Surface a top-level error summary whenever validation fails so
		   the user gets feedback even if the per-field error is far
		   above the Save button (common in the modal scrollable body).
		   The details list spells out each failure as a link that jumps
		   to the field; the template scrolls/focuses the banner. */
		const validationErrors = validateForm();
		const errorCount = Object.keys(validationErrors).length;
		if (errorCount > 0) {
			showSubmitError(
				errorCount === 1
					? 'Please fix the highlighted error before saving:'
					: `Please fix the ${errorCount} highlighted errors before saving:`,
				buildErrorDetails(validationErrors)
			);
			return;
		}

		/* Swapping the checklist deletes the current one's items, and with
		   them every tick, rating and note. Ask first — before anything is
		   written — and send the answer, which the server requires. */
		const checklistChanges = isEditMode && Boolean(checklistTemplateId) && checklistTemplateId !== originalChecklistTemplateId;
		const progressAtRisk = checklistChanges ? (attachedChecklist?.progressCount ?? 0) : 0;
		if (progressAtRisk > 0 && !(await confirmReplace(progressAtRisk))) return;
		const replaceProgress = progressAtRisk > 0;

		setIsSubmitting(true);

		/* When the picker is set to anything other than 'none' AND we're
		   in create mode, route the create through the recurrence-series
		   endpoint instead of the direct WO/Inspection create. The
		   backend will mint every occurrence up to the rule's horizon
		   (capped at 1 year) and return the first occurrence's id so
		   the caller's onSuccess(id) contract is preserved. */
		const useRecurrenceFlow = !isEditMode && recurrenceMode !== 'none';
		const requireRecurrenceAnchor = useRecurrenceFlow && !scheduledDate;
		if (requireRecurrenceAnchor) {
			/* Highlight the date input inline too — AssignmentSection reads
			   errors.scheduledDate for the work_order form type. (Inspections
			   never hit this path: their date is validated as required.) */
			const anchorMessage = 'Scheduled date is required for a recurring schedule';
			setErrors({ scheduledDate: anchorMessage });
			showSubmitError(
				'A scheduled date is required to set a recurring schedule.',
				[{ id: 'scheduledDate', message: anchorMessage }]
			);
			setIsSubmitting(false);
			return;
		}

		try {
			/* Ask who this save would email before anything is written.
			   Deliberately the FIRST thing in the try: an admin who backs out
			   at the prompt should leave nothing behind, and the contact
			   persistence below is a real write. Resolves to {} for non-admins
			   and for saves that would email nobody — in both cases no prompt
			   is shown and the payload carries no notification flags.

			   Skipped for the recurrence flow: the series endpoint mints its
			   occurrences directly and notifies nobody, so there is no send to
			   ask about. */
			const notifyDecision = useRecurrenceFlow ? {} : await requestNotifyDecision({
				kind: formType,
				id: editId ?? null,
				companyId: companyId || null,
				assigneeId: assigneeId || null,
				additionalAssigneeIds,
				notifyClient,
				contactEmail: contactEmail.trim() || null,
				contactName: contactName.trim() || null,
			});
			if (!notifyDecision) return;

			/* The shared payload pieces — both work orders and inspections
			   carry the same Customer & Location, Customer Contact,
			   Assignment, Billing, and Settings data after the unified
			   refactor. The two branches below build their type-specific
			   create payloads with the same source state values. */

			/* Resolve the contact mode at submit time. The same logic
			   applies to both formTypes because both now use the 3-mode
			   ContactFieldset.

			   Either picker mode can produce either kind of selection now:
			   "Existing Contact" lists the customer's saved contacts AND
			   their platform users, so it resolves through whichever of the
			   two ids the fieldset set. Keying off the ids rather than off
			   the mode keeps those two paths from having to be duplicated. */
			let resolvedContactId: number | undefined;
			if (contactMode !== 'new_contact' && selectedContactId) {
				resolvedContactId = Number(selectedContactId);
			} else if (contactMode !== 'new_contact' && contactUserId && companyId) {
				/* Find-or-create the customer's contact record for the picked
				   platform user so the WO carries a real contact_id (which
				   also powers the contact-is-assignee workflow gating). The
				   backend reuses the existing linked contact for this
				   (company, user) pair instead of minting a duplicate. */
				const contactRes = await contactService.create({
					company_id: companyId,
					user_id: contactUserId,
				});
				const userContact = contactRes.data?.data ?? contactRes.data;
				if (userContact?.id) resolvedContactId = userContact.id;
			} else if (contactMode === 'new_contact' && (newContactFirstName.trim() || newContactLastName.trim())) {
				/* Persist the new contact first so the resulting record carries
				   a contact_id back to it. Shares persistNewContact with the
				   fieldset's "Save contact to customer" button, which also
				   makes it a no-op when the user already saved it there. */
				const newId = await persistNewContact();
				if (newId) resolvedContactId = Number(newId);
			}

			/* Compose the resolved contact name for both new + existing
			   contact paths so the snapshot fields stay populated. */
			const resolvedContactName = (contactMode === 'new_contact' && (newContactFirstName.trim() || newContactLastName.trim()))
				? `${newContactFirstName.trim()} ${newContactLastName.trim()}`.trim()
				: contactName.trim();

			if (formType === 'work_order') {
				const payload: CreateWorkOrderData = {
					projectName: projectName.trim(),
					description: description.trim(),
					businessLine: businessLine as CreateWorkOrderData['businessLine'],
					billingType: 'fixed_price',
					companyId,
					locationId,
					assigneeId,
				};

				/* CMS-managed Sub Type slug. Only sent when set so
				   PATCH-style edits don't accidentally clear an
				   existing value. */
				if (subType) (payload as any).subType = subType;

				/* Require Hours. Always sent (not gated on truthiness) so
				   UNticking the box in edit mode actually clears the flag —
				   the PATCH validator ignores absent keys, so omitting it
				   when false would silently leave hour tracking on. */
				(payload as any).requiresHours = tracksHours;

				/* Bucket of hours → work_orders.allocated_hours. Skipped on
				   a project child, where the create page's own allocation
				   panel owns that column and sends it below; sending both
				   would have two inputs racing for one field.

				   Only sent while hour tracking is ON. That column is shared
				   with the project-hours accounting, and the detail page's
				   edit modal doesn't know whether the WO it is editing is a
				   project child — so posting a null here whenever the box is
				   unticked would silently wipe a live project allocation.
				   Leaving the stored value alone instead is harmless: nothing
				   reads it as a bucket unless tracking is on. An empty input
				   while tracking IS on still posts null, so clearing the
				   bucket works. */
				if (tracksHours && !hoursBucketOwnedByProject) {
					payload.allocatedHours = hoursBucket.trim() !== '' ? Number(hoursBucket) : null;
				}

				/* Per-assignee hours. The backend accepts these as
				   snake_case (api.ts handles the conversion).
				   assigneeHours is sent only when at least one row has
				   a non-empty value so we don't replace existing pivot
				   rows with nulls on a non-hours save. */
				const hoursEntries = Object.entries(assigneeHours).filter(([, v]) => v !== '' && v != null);
				if (hoursEntries.length > 0) {
					payload.assigneeHours = Object.fromEntries(
						hoursEntries.map(([uid, v]) => [uid, Number(v)])
					);
				}
				if (projectAddress.trim()) payload.projectAddress = projectAddress.trim();
				// Always send priority. Omitting it when 'medium' is harmless on
				// create (backend defaults to medium) but silently drops the change
				// in EDIT mode — the update validator uses `sometimes`, so an absent
				// field leaves the stored (non-medium) priority unchanged.
				payload.priority = priority as CreateWorkOrderData['priority'];
				if (scheduledDate) payload.scheduledDate = scheduledDate;

				/* Convert local times → UTC using the picked timezone.
				   Date portion comes from the scheduled date when set,
				   else today (so an end_time without a date is still
				   parsable). */
				const woDateForTime = scheduledDate || new Date().toISOString().split('T')[0];
				if (startTime) payload.startTime = localToUtcIso(woDateForTime, startTime, scheduleTimezone);
				if (endTime) payload.endTime = localToUtcIso(woDateForTime, endTime, scheduleTimezone);

				/* Contact fields — the resolved values come from the shared
				   3-mode picker logic above. In edit mode, cleared fields are
				   sent as explicit nulls: the PATCH validator ignores absent
				   keys, so omitting them would silently keep the old contact. */
				if (resolvedContactName) payload.contactName = resolvedContactName;
				else if (isEditMode) payload.contactName = null;
				if (contactPhone.trim()) payload.contactPhone = contactPhone.trim();
				else if (isEditMode) payload.contactPhone = null;
				if (contactEmail.trim()) payload.contactEmail = contactEmail.trim();
				else if (isEditMode) payload.contactEmail = null;
				if (resolvedContactId) payload.contactId = resolvedContactId;
				else if (isEditMode) payload.contactId = null;

				/* Billing fields */
				if (billingInfo.length > 0) payload.billingInfo = billingInfo;
				if (billingName.trim()) payload.billingName = billingName.trim();
				if (billingAddress.trim()) payload.billingAddress = billingAddress.trim();
				if (billingAddressLine2.trim()) payload.billingAddressLine2 = billingAddressLine2.trim();
				if (billingCity.trim()) payload.billingCity = billingCity.trim();
				if (billingState.trim()) payload.billingState = billingState.trim();
				if (billingZip.trim()) payload.billingZip = billingZip.trim();
				if (billingNotes.trim()) payload.billingNotes = billingNotes.trim();
				payload.signatureRequired = signatureRequired;

				/* Estimate fields — empty strings stay unset so the
				   backend's nullable validation accepts the payload. */
				if (estimatedCost.trim() !== '') (payload as any).estimatedCost = parseFloat(estimatedCost);
				if (estimateMin.trim() !== '') (payload as any).estimateMin = parseFloat(estimateMin);
				if (estimateMax.trim() !== '') (payload as any).estimateMax = parseFloat(estimateMax);

				if (additionalAssigneeIds.length > 0) {
					payload.additionalAssigneeIds = additionalAssigneeIds;
				}

				/* Create-only payload extras — both blocks below are gated on
				   !isEditMode because edit mode never re-parents a WO (project
				   and follow-up links are immutable after creation; they go
				   through dedicated endpoints instead). */
				if (!isEditMode) {
					/* Project link: the detail page reaches this code path
					   via "/work-orders/create?project_work_order_id=" — the
					   prop is wired up in the create page. */
					if (projectWorkOrderId) {
						(payload as any).projectWorkOrderId = projectWorkOrderId;
						/* When the page level supplies an allocation value for
						   the parent project's hour pool, forward it as part
						   of the same payload. The backend records this on
						   work_orders.allocated_hours, which the project
						   detail page then sums against the project total. */
						if (projectAllocatedHours != null && Number.isFinite(projectAllocatedHours) && projectAllocatedHours >= 0) {
							payload.allocatedHours = projectAllocatedHours;
						}
					}

					/* Follow-Up parent IDs — at most one is set. Flowed in via
					   initialData when the form was opened from a "Create
					   Follow-Up" kebab action on either a WO or an inspection. */
					if (parentWorkOrderId) payload.parentWorkOrderId = parentWorkOrderId;
					if (parentInspectionId) payload.parentInspectionId = parentInspectionId;
				}

				/* Sync billing address back to the company when the user
				   explicitly opted into editing the auto-filled address. */
				if (isBillingEditable && companyId) {
					try {
						await api.patch(`/companies/${companyId}`, {
							billing_address: billingAddress.trim(),
							billing_address_line_2: billingAddressLine2.trim(),
							billing_city: billingCity.trim(),
							billing_state: billingState.trim(),
							billing_zip: billingZip.trim(),
						});
					} catch (companyErr) {
						console.error('[JobForm] Failed to update company billing address:', companyErr);
					}
				}

				let workOrderId: string | number;
				if (isEditMode && editId) {
					await workOrderService.update(String(editId), { ...payload, ...notifyDecision });
					workOrderId = editId;
				} else if (useRecurrenceFlow) {
					/* Route through the recurrence series endpoint. Jobs are
					   created on their date now, not a year up front, so the
					   response carries the first job's id only when the start
					   date is today or earlier. Otherwise there is nothing to
					   open yet and the user lands on the series' page, which
					   shows when the first job will appear.
					   checklistTemplateId is sent as a metadata field;
					   the controller strips it before WO insert and
					   re-applies it as a snapshot to every occurrence. */
					const seriesResp = await recurrenceSeriesService.create({
						type: 'work_order',
						rule: recurrenceRule,
						template: {
							...(payload as unknown as Record<string, unknown>),
							checklistTemplateId: Number(checklistTemplateId),
						},
					});
					const out = (seriesResp.data as any)?.data ?? seriesResp.data;
					/* The series already snapshotted the checklist onto any
					   job it created, so skip the reassign below. */
					invalidateAfterSave();
					if (out.first_occurrence_id) onSuccess(out.first_occurrence_id);
					else navigate(`/recurring-work-orders/${out.series.id}`);
					return;
				} else {
					const response = await workOrderService.create({ ...payload, ...notifyDecision });
					const created = (response.data as any)?.data ?? response.data;
					workOrderId = created.id;
				}

				/* Checklist assignment — only call when the selection
				   changed. Same semantics as before the refactor. */
				if (checklistTemplateId && checklistTemplateId !== originalChecklistTemplateId) {
					try {
						await checklistInstanceService.assignToWorkOrder(String(workOrderId), {
							checklistId: Number(checklistTemplateId),
							replaceProgress,
						});
					} catch (assignErr) {
						console.error('[JobForm] Failed to assign checklist:', assignErr);
						/* On an edit the record saved but the checklist didn't
						   change — say so and stay, so it can be retried. (A
						   create carries on: staying would invite a duplicate.) */
						if (isEditMode) {
							invalidateAfterSave();
							showSubmitError(`Your changes were saved, but the checklist wasn't changed: ${apiErrorMessage(assignErr, 'please try again.')}`);
							return;
						}
					}
				}

				invalidateAfterSave();
				setIsDirty(false);
				onSuccess(workOrderId);
				return;
			}

			/* ----- Inspection branch -----
			   The unified backend means the inspection payload now
			   carries the same descriptive fields the work order
			   payload does. */
			const payload: CreateInspectionData = {
				companyId,
				locationId,
				inspectionDate: scheduledDate,
				assigneeId,
			};

			if (projectAddress.trim()) payload.projectAddress = projectAddress.trim();
			// Always send priority (see work-order branch) so setting it back to
			// 'medium' in edit mode is persisted instead of silently dropped.
			payload.priority = priority as CreateInspectionData['priority'];

			/* CMS-managed Business Line. Inspections grew this column in
			   2026-05-24 so the picker is shared with work orders; the
			   Sub Type picker is deliberately not offered here. */
			if (businessLine) (payload as any).businessLine = businessLine;

			/* Contact fields — same explicit-null-on-edit semantics as the
			   work order branch so clearing the picker actually unlinks. */
			if (resolvedContactName) payload.contactName = resolvedContactName;
			else if (isEditMode) payload.contactName = null;
			if (contactPhone.trim()) payload.contactPhone = contactPhone.trim();
			else if (isEditMode) payload.contactPhone = null;
			if (contactEmail.trim()) payload.contactEmail = contactEmail.trim();
			else if (isEditMode) payload.contactEmail = null;
			if (resolvedContactId) payload.contactId = resolvedContactId;
			else if (isEditMode) payload.contactId = null;

			/* Time conversion uses the inspection date as the date portion. */
			if (startTime) payload.startTime = localToUtcIso(scheduledDate, startTime, scheduleTimezone);
			if (endTime) payload.endTime = localToUtcIso(scheduledDate, endTime, scheduleTimezone);

			if (generalNotes.trim()) payload.generalNotes = generalNotes.trim();

			/* No billing fields here — the inspection form has no Billing
			   section, so there's nothing to send. */

			/* Settings. The legacy `frequency` string is no longer sent to
			   /inspections directly; recurring schedules are created via the
			   recurrence-series endpoint when useRecurrenceFlow is true.
			   No signatureRequired — inspections are never signed. */
			/* "Send to Client" — always sent so unchecking it is persisted,
			   not just checking it. */
			payload.notifyClient = notifyClient;

			/* No estimate fields here — inspections don't carry pricing.
			   The Settings section hides the cost/range inputs on
			   inspection forms, so there is nothing to submit. */

			/* Additional assignees */
			if (additionalAssigneeIds.length > 0) {
				payload.additionalAssigneeIds = additionalAssigneeIds;
			}

			let inspectionId: string | number;
			if (isEditMode && editId) {
				await inspectionService.update(String(editId), { ...payload, ...notifyDecision });
				inspectionId = editId;
			} else if (useRecurrenceFlow) {
				/* Same routing pattern as the WO branch — the first
				   inspection exists only if the start date has arrived;
				   otherwise land on the series' page. checklist
				   template id rides along on the template so the
				   controller can attach it to every minted occurrence. */
				const seriesResp = await recurrenceSeriesService.create({
					type: 'inspection',
					rule: recurrenceRule,
					template: {
						...(payload as unknown as Record<string, unknown>),
						checklistTemplateId: Number(checklistTemplateId),
					},
				});
				const out = (seriesResp.data as any)?.data ?? seriesResp.data;
				invalidateAfterSave();
				if (out.first_occurrence_id) onSuccess(out.first_occurrence_id);
				else navigate(`/recurring-inspections/${out.series.id}`);
				return;
			} else {
				const response = await inspectionService.create({ ...payload, ...notifyDecision });
				const created = (response.data as any)?.data ?? response.data;
				inspectionId = created.id;
			}

			if (checklistTemplateId && checklistTemplateId !== originalChecklistTemplateId) {
				try {
					await checklistInstanceService.assignToInspection(String(inspectionId), {
						checklistId: Number(checklistTemplateId),
						replaceProgress,
					});
				} catch (assignErr) {
					console.error('[JobForm] Failed to assign checklist:', assignErr);
					/* Same as the work order path above. */
					if (isEditMode) {
						invalidateAfterSave();
						showSubmitError(`Your changes were saved, but the checklist wasn't changed: ${apiErrorMessage(assignErr, 'please try again.')}`);
						return;
					}
				}
			}

			invalidateAfterSave();
			setIsDirty(false);
			onSuccess(inspectionId);
		} catch (err: any) {
			const action = isEditMode ? 'update' : 'create';
			const msg = err?.data?.message || (err instanceof Error ? err.message : `Failed to ${action} ${formType === 'work_order' ? 'work order' : 'inspection'}`);
			/* Route through showSubmitError so the banner is scrolled into
			   view — an API failure otherwise renders silently off-screen
			   above a long form. */
			showSubmitError(msg);
		} finally {
			setIsSubmitting(false);
		}
	};

	/* ============================================
	 * RENDER — Build section prop bundles + delegate to JobFormTemplate
	 * ============================================ */

	/* The shared styling tokens are passed through to every section
	   molecule so the look stays consistent across cards. */
	const styling = { labelClass, inputClass, normalBorder, errorBorder };

	return (
		<>
		<JobFormTemplate
			className={['job-form', className ?? ''].filter(Boolean).join(' ')}
			dataRef="organism-job-form"
			formType={formType}
			isSubmitting={isSubmitting}
			isEditMode={isEditMode}
			submitError={submitError}
			submitErrorDetails={submitErrorDetails}
			submitErrorNonce={submitErrorNonce}
			onSubmit={handleSubmit}
			onChange={() => setIsDirty(true)}

			projectInformation={{
				/* Project name + description live on work_orders only.
				   The inspection form still renders the section for
				   business_line but drops the title/description fields
				   via showProjectFields=false and the Sub Type picker
				   via showSubType=false. */
				showProjectFields: formType === 'work_order',
				showSubType: formType === 'work_order',
				projectName,
				onProjectNameChange: setProjectName,
				description,
				onDescriptionChange: setDescription,
				businessLine,
				onBusinessLineChange: setBusinessLine,
				subType,
				onSubTypeChange: setSubType,
				businessLines,
				errors,
				...styling,
			}}

			customerLocation={{
				formType,
				companyId,
				onCompanyChange: handleCompanyChange,
				locationId,
				onLocationChange: setLocationId,
				projectAddress,
				onProjectAddressChange: setProjectAddress,
				errors,
				/* Lock the Company dropdown for two cases:
				   1. Follow-Up flow (lockCompany prop from caller) — the
				      new WO must stay with the parent's customer.
				   2. Child-WO-under-a-Project — the WO inherits its
				      company from the parent Project. */
				lockCompany: lockCompany || Boolean(projectWorkOrderId),
				/* Lock the Building dropdown only for the project-child
				   case — Follow-Ups still let the user pick a building
				   that may differ from the parent record's. */
				lockLocation: Boolean(projectWorkOrderId),
				/* Only roles the API lets create a location get the inline
				   "Add a New Building" link — techs would just hit a 403. */
				onAddBuilding: hasAnyRole([...LOCATION_MANAGE_ROLES]) ? () => setAddBuildingOpen(true) : undefined,
				refreshToken: buildingsRefreshToken,
				...styling,
			}}

			onSiteContact={{
				companyId,
				contactMode,
				onContactModeChange: setContactMode,
				contactName,
				onContactNameChange: setContactName,
				contactPhone,
				onContactPhoneChange: setContactPhone,
				contactEmail,
				onContactEmailChange: setContactEmail,
				newContactFirstName,
				onNewContactFirstNameChange: setNewContactFirstName,
				newContactLastName,
				onNewContactLastNameChange: setNewContactLastName,
				newContactTitle,
				onNewContactTitleChange: setNewContactTitle,
				contactUserId,
				onContactUserIdChange: setContactUserId,
				selectedContactId,
				onSelectedContactIdChange: setSelectedContactId,
				onSaveNewContact: persistNewContact,
				/* "+ Quick Add User" on the contact pickers — the new person
				   becomes the contact, defaulting to the selected customer. */
				quickAdd: {},
				labelClass,
				inputClass,
				normalBorder,
			}}

			assignment={{
				formType,
				inspector: formType === 'inspection' && user ? {
					firstName: user.firstName,
					lastName: user.lastName,
					email: user.email,
				} : null,
				assigneeId,
				onAssigneeChange: setAssigneeId,
				additionalAssigneeIds,
				onAdditionalAssigneesChange: setAdditionalAssigneeIds,
				/* Hours mode swaps the assignee pickers for editable hour
				   rows. Driven by the "Require Hours" flag, which any work
				   order can opt into and which the T&M sub-type forces on.
				   Inspections never track hours. */
				tmMode: tracksHours,
				assigneeHours,
				onAssigneeHoursChange: setAssigneeHours,
				hoursBucketValue,
				isTimeAndMaterials,
				/* "+ Quick Add User" on every assignee picker (plain and hours
				   rows): anyone missing can be added on the spot, defaulting
				   to this record's business, and lands on the crew. */
				quickAdd: { companyId: companyId || null },
				/* Moving an existing work order to someone else needs Can
				   Reassign Work Orders, and the server refuses it without.
				   Creating one is not a move, so only edits lock. */
				primaryLocked: formType === 'work_order' && isEditMode && !user?.canReassign,
				/* The opt-in itself. Omitted on inspections so the section
				   renders exactly as it did before for them. */
				requireHours: formType === 'work_order' ? {
					checked: requiresHours,
					onCheckedChange: setRequiresHours,
					locked: isTimeAndMaterials,
					lockedNote: '(required for T&M)',
					bucketHours: hoursBucket,
					onBucketHoursChange: setHoursBucket,
					showBucket: !hoursBucketOwnedByProject,
					bucketRequired: isTimeAndMaterials,
					bucketElsewhereNote: 'This work order draws from the hours entered in Project Assignment above.',
				} : undefined,
				priority,
				onPriorityChange: setPriority,
				priorityOptions: WORK_ORDER_PRIORITY_OPTIONS,
				scheduledDate,
				onScheduledDateChange: setScheduledDate,
				startTime,
				onStartTimeChange: setStartTime,
				endTime,
				onEndTimeChange: setEndTime,
				timezone: scheduleTimezone,
				onTimezoneChange: setScheduleTimezone,
				timezoneOptions: SCHEDULE_TIMEZONE_OPTIONS,
				errors,
				...styling,
			}}

			inspectionNotes={formType === 'inspection' ? {
				generalNotes,
				onGeneralNotesChange: setGeneralNotes,
				labelClass,
				normalBorder,
			} : undefined}

			/* Billing is work-order-only.

			   Inspections don't carry billing at all — they're an
			   observation/reporting record, and any charge that comes
			   out of one is raised on the follow-up work order instead.

			   It's also omitted when a WO is being created as a child of
			   a Project Work Order: the project owns billing
			   classification + notes for all its children, and surfacing
			   it per-WO would let users put each WO in a different
			   billing bucket, which the project model explicitly
			   avoids. */
			billing={(formType === 'inspection' || projectWorkOrderId) ? undefined : {
				billingInfo,
				onBillingInfoToggle: toggleBillingInfo,
				billingInfoOptions: BILLING_INFO_OPTIONS,
				billingInfoError: errors.billingInfo,
				isAddressEditable: isBillingEditable,
				onEditAddress: () => setIsBillingEditable(true),
				billingName,
				onBillingNameChange: setBillingName,
				billingAddress,
				onBillingAddressChange: setBillingAddress,
				billingAddressLine2,
				onBillingAddressLine2Change: setBillingAddressLine2,
				billingCity,
				onBillingCityChange: setBillingCity,
				billingState,
				onBillingStateChange: setBillingState,
				billingZip,
				onBillingZipChange: setBillingZip,
				billingPhone,
				onBillingPhoneChange: setBillingPhone,
				billingEmail,
				onBillingEmailChange: setBillingEmail,
				billingNotes,
				onBillingNotesChange: setBillingNotes,
				labelClass,
				inputClass,
				normalBorder,
			}}

			settings={{
				formType,
				notifyClient,
				onNotifyClientChange: setNotifyClient,
				signatureRequired,
				onSignatureRequiredChange: setSignatureRequired,
				signatureNote: formType === 'work_order' && businessLine === 'construction'
					? '(auto-enabled for construction)'
					: null,
				/* Slim mode hides Frequency + Estimate when this WO is a
				   child of a Project — those concerns live on the
				   project, not the per-WO record. Signature Required
				   and Checklist stay visible since they can legitimately
				   vary across child WOs. */
				slim: Boolean(projectWorkOrderId),
				recurrenceMode,
				recurrenceRule,
				onRecurrenceChange: ({ mode, rule }) => {
					setRecurrenceMode(mode);
					setRecurrenceRule(rule);
				},
				/* The scheduled-date anchor drives the picker's preset
				   labels ("Weekly on Monday" etc.) and seeds the rule's
				   start_date on save. */
				recurrenceStartDate: scheduledDate,
				/* Only allow recurrence configuration on create + duplicate
				   flows — editing an existing record is for that single
				   occurrence only. To change the series schedule itself,
				   the user goes to Recurring Work Orders / Recurring
				   Inspections in the sidebar. */
				allowRecurrence: !isEditMode,
				checklistTemplateId,
				onChecklistTemplateIdChange: setChecklistTemplateId,
				assignableChecklists: checklistOptions,
				/* Surface the required-field error on the Checklist
				   dropdown inline (red border + message) so the user
				   sees it next to the field rather than only in the
				   top banner. */
				errors,
				estimate: { estimatedCost, estimateMin, estimateMax },
				onEstimateChange: (next) => {
					setEstimatedCost(next.estimatedCost);
					setEstimateMin(next.estimateMin);
					setEstimateMax(next.estimateMax);
				},
				labelClass,
				inputClass,
				normalBorder,
				errorBorder,
			}}
		/>
		{/* Inline "Add a New Building" modal, opened from the Building
		    dropdown. The customer is locked to the one already picked on this
		    form so the new building shows up in that same dropdown. */}
		<AddBuildingModal
			isOpen={addBuildingOpen}
			onClose={() => setAddBuildingOpen(false)}
			lockedCompanyId={companyId}
			onCreated={handleBuildingCreated}
		/>
		{/* The "Notify users" prompt. Null until a save would actually email
		    someone and the user is admin-tier — see useNotifyPrompt. */}
		{notifyPrompt}
		{/* "Replace this checklist?" — renders nothing until a save would replace one with work on it. */}
		{replacePrompt}
		{/* Leaving with a half-filled form — a sidebar link, the back button,
		    a refresh — asks first. */}
		<UnsavedChangesPrompt when={isDirty} />
		</>
	);
};

export default JobForm;
